import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState
} from "react";

import
{
    isFailed,
    isSettled,
    isSucceeded,
    SystemStatus,
    type PaginationRequest,
    type RpcFailedJob,
    type RpcJob,
    type RpcPendingJob
} from '@ews/react-data';

import
{
    retrievePersistentData
} from '../Persistence';

import
{
    type LoadingContextType
} from "../Loading";

import EventEmitter from "events";

import
{
    AuthRequest,
    Channel,
    ReactTopics,
    Websocket,
    client as websocketClient
} from "@ews/websocket-service";

import
{
    env
} from '../Environment';

import
{
    isUserImpersonated
} from "../Authorization";

export const toggle: LoadingContextType = () => { };

export type ReactDataClientError = {
    url: string,
    body: {
        errorCode: number,
        error: string;
    };
};

class ReactDataProvider extends EventEmitter
{

    protected _throttling: number = 0;

    protected client: ReturnType<typeof websocketClient>;
    protected channel: Channel;
    protected status: SystemStatus = SystemStatus.UNKNOWN;

    protected subscriptions: Record<string, true> = {};
    protected cache: Record<string, any> = {};

    constructor(protected serviceUri: string, protected heartbeatInterval: number = 10000)
    {
        super();
        [this.channel, this.client] = this.connect();
    }

    protected throttling(increase: number)
    {
        if (increase) {
            this._throttling = Math.min(this._throttling += increase, 10000);
        } else {
            this._throttling = 0;
        }

        return this._throttling;
    }

    protected setStatus(status: SystemStatus)
    {
        this.status = status;
        this.emit("status", this.status);
    }

    getStatus = () => this.status;

    protected bindChannel()
    {
        this.channel.on("message:publish", (msg: any, ...topics: string[]) =>
        {
            for (const topic of topics) {
                this.cache[topic] = msg;
                this.emit(`resolve:${topic}`, msg);
            }
        });

        this.channel.on("message:auth", (response: AuthRequest) =>
        {
            console.log("message:auth", response);
            console.log(retrievePersistentData('state: impersonationState'));
            if (response.type === "token") this.emit("channel:unlocked");
            this.emit("auth:response", response);
        });

        this.channel.on("error", (err) => console.log(err));
        this.channel.on("close", () => setTimeout(() => this.connect(), this.throttling(1000)));
        this.channel.on("open", () =>
        {
            this.setStatus(SystemStatus.ONLINE);
            this.throttling(0);
        });

    }
    protected subscribeChannel()
    {
        const topics = Object.keys(this.subscriptions);
        if (topics.length) this.client.bind(...topics);
    }

    protected connect(): [Channel, ReturnType<typeof websocketClient>]
    {
        this.setStatus(SystemStatus.OFFLINE);

        // if (this.channel) {
        //     this.channel.close();
        // }

        const websocket = new Websocket(this.serviceUri);

        this.channel = new Channel(websocket);
        this.client = websocketClient(this.channel);

        this.bindChannel();
        this.once("channel:unlocked", () => this.subscribeChannel());

        this.client.setupHeartbeat(this.heartbeatInterval);

        return [this.channel, this.client];

    }

    subscribe(topic: string, callback: (data: any) => void)
    {
        this.on(`resolve:${topic}`, callback);
        this.subscriptions[topic] = true;
    }

    unsubscribe(topic: string, callback: (data: any) => void)
    {
        this.off(`resolve:${topic}`, callback);
    }

    getTopicValue(topic: string, defaultValue: any)
    {
        return (typeof this.cache[topic] === 'undefined') ? defaultValue : this.cache[topic];
    }

    bind(topic: string, callback: (data: any) => void)
    {
        this.subscribe(topic, callback);

        if (topic in this.cache) {
            if (this.cache[topic]) callback(this.cache[topic]);
        } else {
            this.cache[topic] = undefined;
            this.client.bind(topic);
        }
    }

    fetch<R>(filterCriteria: PaginationRequest, topic: string, loadingToggle: LoadingContextType = toggle): Promise<R>
    {
        return new Promise((resolve, reject) =>
        {
            this.channel.once(`message:fetch:${topic}`, (data: R) =>
            {
                if (!data) {
                    console.warn(`Falsy data detected in handler for message:fetch:${topic}`, data);
                }

                if (typeof data === 'string') {
                    reject(data);
                } else {
                    resolve(data);
                }
                loadingToggle(false);
            });

            loadingToggle(true);
            this.client.fetch(filterCriteria, topic);
        });
    };

    create<T, R = T>(data: T, topic: string, loadingToggle: LoadingContextType = toggle): Promise<R>
    {

        if (isUserImpersonated()) return Promise.reject("OPER_RESULT_NOT_PERMITTED_IN_IMPERSONATE_MODE");

        return new Promise((resolve, reject) =>
        {
            this.channel.once(`message:created:${topic}`, (data: R) =>
            {
                if (!data) {
                    console.warn(`Falsy data detected in handler for message:created:${topic}`, data);
                }

                if (typeof data === 'string') {
                    reject(data);
                } else {
                    resolve(data);
                }
                loadingToggle(false);
            });

            loadingToggle(true);
            this.client.create(data, topic);
        });
    };

    modify<T, R = T>(data: T, topic: string, loadingToggle: LoadingContextType = toggle): Promise<R>
    {
        if (isUserImpersonated()) return Promise.resolve(data as unknown as R);

        return new Promise((resolve, reject) =>
        {
            this.channel.once(`message:modified:${topic}`, (data: R) =>
            {
                if (!data) {
                    console.warn(`Falsy data detected in handler for message:modified:${topic}`, data);
                }

                if (typeof data === 'string') {
                    reject(data);
                } else {
                    resolve(data);
                }
                loadingToggle(false);
            });

            loadingToggle(true);
            this.client.modify(data, topic);
        });

    };

    delete<T, R = T>(topic: string, loadingToggle: LoadingContextType = toggle): Promise<R>
    {
        if (isUserImpersonated()) return Promise.reject("OPER_RESULT_NOT_PERMITTED_IN_IMPERSONATE_MODE");

        return new Promise((resolve, reject) =>
        {
            this.channel.once(`message:deleted:${topic}`, (data: R) =>
            {
                if (!data) {
                    console.warn(`Falsy data detected in handler for message:deleted:${topic}`, data);
                }

                if (typeof data === 'string') {
                    reject(data);
                } else {
                    resolve(data);
                }
                loadingToggle(false);
            });

            loadingToggle(true);
            this.client.remove(topic);
        });
    };

    operate<T, R = T>(data: T, topic: string, loadingToggle: LoadingContextType = toggle): Promise<R>
    {
        if (isUserImpersonated()) return Promise.reject("NOT_PERMITTED_IN_IMPERSONATE_MODE");

        return new Promise((resolve, reject) =>
        {
            this.channel.once(`message:confirm:${topic}`, (data: R) =>
            {
                if (!data) {
                    console.warn(`Falsy data detected in handler for message:confirm:${topic}`, data);
                }

                if (typeof data === 'string') {
                    reject(data);
                } else {
                    resolve(data);
                }
                loadingToggle(false);
            });

            loadingToggle(true);
            this.client.operate(data, topic);
        });
    };

    auth(request: AuthRequest, loadingToggle: LoadingContextType = toggle): Promise<AuthRequest>
    {

        if (isUserImpersonated() && ['forgotPassword', 'forgotPasswordRpc'].includes(request.type)) {
            return Promise.reject({ translationKey: "OPER_RESULT_NOT_PERMITTED_IN_IMPERSONATE_MODE" });
        }

        return new Promise((resolve, reject) =>
        {
            this.channel.once('message:auth', async (data: AuthRequest) =>
            {
                if (!data) {
                    console.warn(`Falsy data detected in handler for message:auth`, data);
                }

                if (data.type === 'status' && "error" in data) {
                    reject(data.error);
                } else {
                    resolve(data);
                }

                loadingToggle(false);
            });

            loadingToggle(true);

            this.client.auth(request);
        });
    }

    // TODO: clientSideTimeoutMs default should not be a magic constant, maybe from env?
    rpc<T, R>(rpcName: string, data: T, loadingToggle: LoadingContextType = toggle, clientSideTimeoutMs: number = 30_000): Promise<R>
    {

        if (isUserImpersonated()) return Promise.reject({ translationKey: "OPER_RESULT_NOT_PERMITTED_IN_IMPERSONATE_MODE" });

        return new Promise<R>((resolve, reject) =>
        {
            loadingToggle(true);

            const createTopic = ReactTopics.rpcJobCreate.withParams({ rpcName });

            this.channel.once(`message:created:${createTopic}`, (createdJob: RpcPendingJob) =>
            {
                // console.debug("created job", createdJob);

                const jobId = createdJob.id;
                if (!jobId) {
                    throw new Error(`Created job does not have an id. Is the rpcName '${rpcName}' correct and does it exist on the server?\ncreatedJob: ${JSON.stringify(createdJob)}`);
                }

                // ATTENTION: we leave a subscription on the bind topic -> need unbind (but can get same effect with client.unsubscribe see below)
                const bindTopic = ReactTopics.rpcJob.withParams({ jobId });

                const resolveEventName = `resolve:${bindTopic}`;
                const resolveHandler = (updatedJob: RpcJob<R>) =>
                {
                    // console.debug("resolved job (job got updated)", updatedJob);

                    if (!isSettled(updatedJob)) return;

                    this.off(resolveEventName, resolveHandler);
                    // ATTENTION: unbind would be nice but we get the same effect with client.unsubscribe
                    this.client.unsubscribe(`${bindTopic}`);
                    loadingToggle(false);

                    if (isSucceeded(updatedJob)) {
                        // console.debug("job SUCCEEDED:", updatedJob.result);
                        resolve(updatedJob.result);
                        return;
                    }
                    if (isFailed(updatedJob)) {
                        console.error("ReactDataProvider.rpc: Job failed!", updatedJob.message, updatedJob);
                        reject(updatedJob.endUserMessage ?? updatedJob.message);
                        return;
                    }

                    const _exhaustiveCheck: never = updatedJob;
                    const errorMessage = `ReactDataProvider.rpc: Unexpected settled job state encountered: ${JSON.stringify(updatedJob)}`;
                    console.error(errorMessage);
                    reject(errorMessage);
                };
                this.on(resolveEventName, resolveHandler);
                // ATTENTION: ueberlebt connection drop nicht
                this.client.bind(`${bindTopic}`);  // subscribe passiert im bind

                // alternativ: ueberlebt connection drop aber wird nie unsubscribed bzw wieder hergestellt bei connection drop
                // dataProvider.bind(`${bindTopic}`, resolveHandler);

                if (clientSideTimeoutMs) {
                    setTimeout(() =>
                    {
                        const seconds = (clientSideTimeoutMs / 1000).toFixed(0);
                        resolveHandler({
                            ...createdJob, state: "failed",
                            message: `Client side timeout after ${seconds} seconds.`,
                            endUserMessage: { translationKey: "[REACT_RPC_INVOCATION_TIMEOUT]", substitute: { seconds } }
                        } satisfies RpcFailedJob);
                    }, clientSideTimeoutMs);
                }
            });
            this.client.create(data, `${createTopic}`);
        });
    }
}

export type ProviderInterface = ReactDataProvider;

const reactDataProvider = new ReactDataProvider(
    env.APP_SERVICE_URL,
    env.APP_HEARTBEAT_INTERVAL || 10000
);

type WebsocketContextType = ReactDataProvider;
const WebsocketContext = createContext<WebsocketContextType>(reactDataProvider);

export const useWebsocket = () =>
{
    return useContext(WebsocketContext);
};

export const ReactData: React.FC<{ children: ReactNode; }> = ({ children }) =>
{
    return (
        <WebsocketContext.Provider value={reactDataProvider} >
            {children}
        </WebsocketContext.Provider >
    );
};

export const useTopicData = <T,>(topic: string, defaultValue: T): T =>
{
    const provider = useWebsocket();
    const [topicData, setTopicData] = useState<T>(provider.getTopicValue(topic, defaultValue));

    useEffect(() =>
    {
        provider.bind(`${topic}`, setTopicData);

        return () =>
        {
            provider.unsubscribe(`${topic}`, setTopicData);
        };

    }, [topic, provider]);

    return topicData;
};
